import React, { useState, useCallback, useEffect } from "react";
import { staffQueue } from "../../api";
import { useAuth } from "../../context/AuthContext";
import { Btn, LoadingSpinner } from "../../components/ui";
import Modal from "../../components/ui/Modal";
import useQueuePoller from "../../hooks/useQueuePoller";
import ManualIssueModal from "./ManualIssueModal";
import toast from "react-hot-toast";

// Accessibility helpers
const ACCESS_ICONS  = { simple:"📖", hearing:"👂", visual:"👁", mobility:"♿", communication:"💬" };
const ACCESS_LABELS = { simple:"Easy Reading", hearing:"Hearing Impairment", visual:"Visual Impairment", mobility:"Mobility Assistance", communication:"Communication Assistance" };
const ACCESS_COLORS = {
  simple:        { bg:"rgba(96,165,250,0.12)",  color:"#60A5FA",  border:"rgba(96,165,250,0.3)"  },
  hearing:       { bg:"rgba(167,139,250,0.12)", color:"#A78BFA",  border:"rgba(167,139,250,0.3)" },
  visual:        { bg:"rgba(52,211,153,0.12)",  color:"#34D399",  border:"rgba(52,211,153,0.3)"  },
  mobility:      { bg:"rgba(251,191,36,0.12)",  color:"#FBBF24",  border:"rgba(251,191,36,0.3)"  },
  communication: { bg:"rgba(244,114,182,0.12)", color:"#F472B6",  border:"rgba(244,114,182,0.3)" },
};
const ACCESS_NOTES = {
  mobility:      "Do not call patient to counter — send staff to patient.",
  communication: "Patient has communication difficulties — prepare communication support card.",
};

function AccessibilityBadge({ mode }) {
  const c = ACCESS_COLORS[mode]; const note = ACCESS_NOTES[mode];
  if (!c) return null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      <div style={{ display:"inline-flex", alignItems:"center", gap:"6px", borderRadius:"8px", padding:"5px 12px", fontSize:"12px", fontWeight:"700", letterSpacing:"0.06em", background:c.bg, color:c.color, border:`1px solid ${c.border}` }}>
        {ACCESS_ICONS[mode]} {ACCESS_LABELS[mode].toUpperCase()}
      </div>
      {note && <div style={{ fontSize:"12px", color:c.color, fontStyle:"italic", padding:"6px 10px", borderRadius:"6px", background:c.bg, border:`1px solid ${c.border}`, lineHeight:1.4 }}>⚠ {note}</div>}
    </div>
  );
}

export default function StaffQueuePage() {
  const { user }                           = useAuth();
  const { queue, loading, error, refresh } = useQueuePoller();

  const [calling,       setCalling]       = useState(false);
  const [transferModal, setTransferModal] = useState(false);
  const [manualModal,   setManualModal]   = useState(false);
  const [reannouncing,  setReannouncing]  = useState(false);
  const [isAway,        setIsAway]        = useState(user?.is_away || false);
  const [awaySince,     setAwaySince]     = useState(user?.away_since || null);
  const [awayTimer,     setAwayTimer]     = useState(null);
  const [counterModal,  setCounterModal]  = useState(false);
  const [selectedCounter, setSelectedCounter] = useState(user?.counter || null);

  // Show counter selection on mount if no counter set
  useEffect(() => {
    if (!loading && queue?.station && !selectedCounter) {
      setCounterModal(true);
    }
  }, [loading, queue?.station]);

  // Away timer
  useEffect(() => {
    if (!isAway || !awaySince) { setAwayTimer(null); return; }
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(awaySince).getTime()) / 60000);
      setAwayTimer(diff);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [isAway, awaySince]);

  const handleCallNext = useCallback(async () => {
    if (isAway) return;
    setCalling(true);
    try {
      const { data } = await staffQueue.callNext({ counter_id: selectedCounter?.id || null });
      if (data?.ticket_number) toast.success(`Called: ${data.ticket_number} — ${data.patient_name}`);
      else toast("Queue is empty.", { icon:"ℹ️" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.error || "Failed to call next."); }
    finally     { setCalling(false); }
  }, [refresh, isAway, selectedCounter]);

  const handleCallSpecific = useCallback(async (ticketId) => {
    if (isAway) return;
    setCalling(true);
    try {
      const { data } = await staffQueue.callSpecific({
        ticket_id: ticketId,
        counter_id: selectedCounter?.id || null,
      });
      toast.success(`Called: ${data.ticket_number} — ${data.patient_name}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to call patient.");
    } finally { setCalling(false); }
  }, [refresh, isAway, selectedCounter]);

  const handleReannounce = useCallback(async () => {
    if (!queue?.serving) return;
    setReannouncing(true);
    try { await staffQueue.reAnnounce(queue.serving.id); toast.success("Re-announced."); }
    catch { toast.error("Failed to re-announce."); }
    finally { setReannouncing(false); }
  }, [queue]);

  const handleGoAway = async () => {
    try {
      const { data } = await staffQueue.goAway();
      setIsAway(true); setAwaySince(data.away_since);
      toast("You're on break. Come back when ready.", { icon:"☕" });
    } catch { toast.error("Failed to update status."); }
  };

  const handleImBack = async () => {
    try {
      await staffQueue.imBack();
      setIsAway(false); setAwaySince(null);
      toast.success("Welcome back!");
    } catch { toast.error("Failed to update status."); }
  };

  const handleSelectCounter = async (counter) => {
    try {
      await staffQueue.selectCounter(counter?.id || null);
      setSelectedCounter(counter);
      setCounterModal(false);
      toast.success(counter ? `Now operating ${counter.name}` : "Counter cleared.");
    } catch { toast.error("Failed to select counter."); }
  };

  if (!loading && (error === "No station assigned." || (!queue?.station && !loading))) {
    return (
      <div style={styles.root}>
        <div style={styles.noStation}>
          <span style={{ fontSize:"48px" }}>🚫</span>
          <h2 style={{ color:"#E2E8F0", margin:"16px 0 8px", fontSize:"20px" }}>No station assigned</h2>
          <p style={{ color:"#64748B", fontSize:"14px", maxWidth:"300px", textAlign:"center", lineHeight:"1.6" }}>
            You haven't been assigned to a station yet. Ask your Facility Admin to assign you one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* ── Header ── */}
      <div style={{ ...styles.header, borderBottom: isAway ? "2px solid rgba(234,179,8,0.4)" : "none" }}>
        <div>
          <h1 style={styles.title}>{queue?.station?.name || "My Queue"}</h1>
          <p style={styles.sub}>
            {user?.name}
            {queue?.station?.code && <span style={styles.stationCode}>{queue.station.code}</span>}
            {selectedCounter && <span style={{ ...styles.stationCode, background:"rgba(52,211,153,0.12)", color:"#34D399" }}>{selectedCounter.name}</span>}
          </p>
        </div>
        <div style={styles.headerActions}>
          {/* Away / Back button */}
          {isAway ? (
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <span style={{ fontSize:"13px", color:"#EAB308", background:"rgba(234,179,8,0.1)", border:"1px solid rgba(234,179,8,0.25)", borderRadius:"8px", padding:"6px 12px" }}>
                ☕ On break{awayTimer !== null ? ` — ${awayTimer} min` : ""}
              </span>
              <Btn variant="ghost" size="sm" onClick={handleImBack}>✅ I'm Back</Btn>
            </div>
          ) : (
            <Btn variant="ghost" size="sm" onClick={handleGoAway}>☕ Take a Break</Btn>
          )}
          {selectedCounter && (
            <Btn variant="ghost" size="sm" onClick={() => setCounterModal(true)}>
              Change Counter
            </Btn>
          )}
          <Btn variant="ghost" size="sm" onClick={() => setManualModal(true)}>+ Manual Ticket</Btn>
          <div style={styles.waitingBadge}>
            <span style={styles.waitingNum}>{queue?.waiting_count ?? 0}</span>
            <span style={styles.waitingLabel}>waiting</span>
          </div>
        </div>
      </div>

      {loading && !queue ? <LoadingSpinner message="Loading queue…" /> : (
        <div style={styles.layout}>
          <div style={styles.leftCol}>
            <ServingPanel
              serving={queue?.serving}
              outcomes={queue?.outcomes || []}
              calling={calling}
              reannouncing={reannouncing}
              onCallNext={handleCallNext}
              onReannounce={handleReannounce}
              onTransfer={() => setTransferModal(true)}
              waitingCount={queue?.waiting_count || 0}
              isAway={isAway}
            />
          </div>
          <div style={styles.rightCol}>
            <WaitingList
              tickets={queue?.waiting || []}
              onCallSpecific={handleCallSpecific}
              calling={calling}
              isAway={isAway}
            />
            {queue?.returning?.length > 0 && (
              <ReturningPanel
                returning={queue.returning}
                onCall={handleCallSpecific}
                calling={calling}
                isAway={isAway}
              />
            )}
          </div>
        </div>
      )}

      <TransferModal open={transferModal} onClose={() => setTransferModal(false)} serving={queue?.serving} outcomes={queue?.outcomes || []} onDone={() => { setTransferModal(false); refresh(); }} />
      <ManualIssueModal open={manualModal} onClose={() => setManualModal(false)} onDone={() => { setManualModal(false); refresh(); }} />
      <CounterSelectModal open={counterModal} onClose={() => setCounterModal(false)} station={queue?.station} onSelect={handleSelectCounter} current={selectedCounter} />
    </div>
  );
}

// ── Counter Select Modal ───────────────────────────────────────────────────────
function CounterSelectModal({ open, onClose, station, onSelect, current }) {
  const counters = station?.counters?.filter(c => c.is_active !== false) || [];
  return (
    <Modal open={open} onClose={onClose} title={`Select Your Counter — ${station?.name || ""}`} width="420px">
      <p style={{ fontSize:"13px", color:"#64748B", margin:"0 0 16px" }}>
        Which counter are you operating today?
      </p>
      <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
        {counters.length === 0 ? (
          <div style={{ color:"#64748B", fontSize:"13px" }}>No counters configured for this station.</div>
        ) : counters.map(c => (
          <button key={c.id} onClick={() => onSelect(c)} style={{
            padding:"12px 16px", borderRadius:"10px", cursor:"pointer",
            border:`2px solid ${current?.id === c.id ? "#2563EB" : "rgba(51,65,85,0.6)"}`,
            background: current?.id === c.id ? "rgba(37,99,235,0.12)" : "rgba(30,41,59,0.5)",
            color:"#E2E8F0", fontFamily:"inherit", textAlign:"left",
            display:"flex", justifyContent:"space-between", alignItems:"center",
          }}>
            <span style={{ fontSize:"14px", fontWeight:"600" }}>{c.name}</span>
            <span style={{ fontSize:"11px", color:"#64748B" }}>Counter {c.number}</span>
          </button>
        ))}
      </div>
      <div style={{ marginTop:"12px", paddingTop:"12px", borderTop:"1px solid rgba(37,99,235,0.1)" }}>
        <Btn variant="ghost" onClick={() => onSelect(null)} style={{ width:"100%", justifyContent:"center" }}>
          Continue without counter selection
        </Btn>
      </div>
    </Modal>
  );
}

// ── Serving Panel ─────────────────────────────────────────────────────────────
function ServingPanel({ serving, outcomes, calling, reannouncing, onCallNext, onReannounce, onTransfer, waitingCount, isAway }) {
  return (
    <div style={SP.root}>
      <div style={SP.label}>NOW SERVING</div>
      {serving ? (
        <>
          <div style={SP.ticketNum}>{serving.ticket_number}</div>
          {serving.priority !== "normal" && (
            <div style={{ ...SP.priorityBadge, background: serving.priority === "emergency" ? "rgba(239,68,68,0.15)" : "rgba(234,179,8,0.15)", color: serving.priority === "emergency" ? "#EF4444" : "#EAB308", border:`1px solid ${serving.priority === "emergency" ? "#EF444433" : "#EAB30833"}` }}>
              {serving.priority === "emergency" ? "🚨 EMERGENCY" : "⚡ PRIORITY"}
            </div>
          )}
          {serving.accessibility_mode && serving.accessibility_mode !== "none" && (
            <AccessibilityBadge mode={serving.accessibility_mode} />
          )}
          <div style={SP.patientCard}>
            <div style={SP.patientName}>{serving.patient_name}</div>
            {serving.patient_phone  && <div style={SP.patientDetail}>📱 {serving.patient_phone}</div>}
            {serving.insurance_type && <div style={SP.patientDetail}>🏥 {serving.insurance_type}</div>}
            {serving.reason_for_visit && <div style={SP.patientDetail}>📋 {serving.reason_for_visit}</div>}
            <div style={SP.patientDetail}>⏱ Waiting {Math.round(serving.total_wait_minutes ?? 0)} min</div>
          </div>
          {serving.journey?.length > 0 && (
            <div style={SP.journey}>
              {serving.journey.map((j, i) => (
                <div key={j.id || i} style={SP.journeyStep}>
                  <div style={{ ...SP.journeyDot, background: j.status === "completed" ? "#22C55E" : "#2563EB" }} />
                  <span style={SP.journeyStation}>{j.station_name}</span>
                  {j.outcome_label && <span style={SP.journeyOutcome}>→ {j.outcome_label}</span>}
                </div>
              ))}
            </div>
          )}
          <div style={SP.actions}>
            <Btn variant="primary" onClick={onTransfer} style={{ flex:1, padding:"12px" }}>Complete / Transfer →</Btn>
            <Btn variant="ghost" onClick={onReannounce} disabled={reannouncing} style={{ padding:"12px 16px" }}>{reannouncing ? "…" : "📣"}</Btn>
          </div>
        </>
      ) : (
        <div style={SP.idle}>
          {isAway ? (
            <>
              <div style={SP.idleIcon}>☕</div>
              <div style={{ ...SP.idleText, color:"#EAB308" }}>On Break</div>
              <div style={SP.idleSub}>Return from break to call patients</div>
            </>
          ) : (
            <>
              <div style={SP.idleIcon}>🟢</div>
              <div style={SP.idleText}>Ready</div>
              <div style={SP.idleSub}>{waitingCount > 0 ? `${waitingCount} patient${waitingCount !== 1 ? "s" : ""} waiting` : "Queue is empty"}</div>
              <Btn onClick={onCallNext} disabled={calling || waitingCount === 0 || isAway} style={{ marginTop:"20px", padding:"14px 32px", fontSize:"16px", width:"100%" }}>
                {calling ? "Calling…" : waitingCount > 0 ? "Call Next ▶" : "No one waiting"}
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Waiting List ──────────────────────────────────────────────────────────────
function WaitingList({ tickets, onCallSpecific, calling, isAway }) {
  return (
    <div style={WL.root}>
      <div style={WL.header}>
        <span style={WL.title}>Waiting</span>
        <span style={WL.count}>{tickets.length}</span>
      </div>
      {tickets.length === 0 ? <div style={WL.empty}>No one waiting.</div> : (
        <div style={WL.list}>
          {tickets.map((t, i) => (
            <div key={t.id} style={{ ...WL.row, borderLeft:`3px solid ${t.priority === "emergency" ? "#EF4444" : t.priority === "priority" ? "#EAB308" : "transparent"}` }}>
              <div style={WL.position}>{i + 1}</div>
              <div style={WL.info}>
                <div style={WL.ticketNum}>{t.ticket_number}</div>
                <div style={WL.name}>{t.patient_name}</div>
                {t.insurance_type && <div style={WL.meta}>{t.insurance_type}</div>}
              </div>
              <div style={WL.right}>
                {t.priority !== "normal" && <span style={{ ...WL.priorityChip, color: t.priority === "emergency" ? "#EF4444" : "#EAB308" }}>{t.priority === "emergency" ? "🚨" : "⚡"}</span>}
              </div>
              <button
                onClick={() => onCallSpecific && onCallSpecific(t.id)}
                disabled={calling || isAway}
                style={{
                  background: "rgba(37,99,235,0.15)",
                  border: "1px solid rgba(37,99,235,0.3)",
                  borderRadius: "6px", color: "#60A5FA",
                  fontSize: "11px", fontWeight: "600",
                  padding: "4px 10px", cursor: calling || isAway ? "not-allowed" : "pointer",
                  fontFamily: "inherit", opacity: calling || isAway ? 0.5 : 1,
                  whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                Call ▶
              </button>
              {t.accessibility_mode && t.accessibility_mode !== "none" && (
  <span
    style={WL.accessChip}
    title={ACCESS_LABELS[t.accessibility_mode]}
  >
    {ACCESS_ICONS[t.accessibility_mode]}
  </span>
)}
<div style={WL.waited}>{minutesAgo(t.issued_at)}m</div>
              </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Transfer Modal ────────────────────────────────────────────────────────────
function TransferModal({ open, onClose, serving, outcomes, onDone }) {
  const [selected, setSelected] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const handleTransfer = async () => {
    if (!selected || !serving) return;
    setLoading(true);
    try {
      await staffQueue.transfer(serving.id, { outcome_id: selected });
      const outcome = outcomes.find(o => o.id === selected);
      toast.success(outcome?.is_exit ? `${serving.ticket_number} — visit complete.` : `${serving.ticket_number} transferred.`);
      setSelected(null); onDone();
    } catch (e) { toast.error(e.response?.data?.error || "Transfer failed."); }
    finally { setLoading(false); }
  };
  const VS = {
    default: { border:"rgba(51,65,85,0.8)", bg:"rgba(30,41,59,0.6)" },
    success: { border:"rgba(34,197,94,0.4)",  bg:"rgba(34,197,94,0.08)"  },
    warning: { border:"rgba(234,179,8,0.4)",  bg:"rgba(234,179,8,0.08)"  },
    danger:  { border:"rgba(239,68,68,0.4)",  bg:"rgba(239,68,68,0.08)"  },
  };
  return (
    <Modal open={open} onClose={onClose} title="Select Outcome" width="480px">
      {serving && (
        <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
          <div style={TM.patientBar}><span style={TM.ticketNum}>{serving.ticket_number}</span><span style={TM.patientName}>{serving.patient_name}</span></div>
          <p style={{ fontSize:"13px", color:"#64748B", margin:0 }}>Select what happens next for this patient:</p>
          <div style={TM.outcomeGrid}>
            {outcomes.map(o => {
              const vs = VS[o.color] || VS.default; const isSel = selected === o.id;
              return (
                <button key={o.id} onClick={() => setSelected(o.id)} style={{ ...TM.outcomeBtn, border:`2px solid ${isSel ? "#2563EB" : vs.border}`, background: isSel ? "rgba(37,99,235,0.2)" : vs.bg }}>
                  <div style={TM.outcomeLabelEn}>{o.label_en}</div>
                  {o.is_exit ? <div style={TM.outcomeMeta}>✓ End of visit</div> : o.next_station__name ? <div style={TM.outcomeMeta}>→ {o.next_station__name}</div> : null}
                </button>
              );
            })}
          </div>
          {outcomes.length === 0 && <div style={{ color:"#EF4444", fontSize:"13px" }}>No outcomes configured. Ask admin to add outcomes.</div>}
          <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end", paddingTop:"8px", borderTop:"1px solid rgba(37,99,235,0.1)" }}>
            <Btn variant="ghost" onClick={onClose} disabled={loading}>Cancel</Btn>
            <Btn onClick={handleTransfer} disabled={!selected || loading}>{loading ? "Processing…" : "Confirm →"}</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function minutesAgo(isoString) {
  if (!isoString) return 0;
  return Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
}

function ReturningPanel({ returning, onCall, calling, isAway }) {
  return (
    <div style={RP.root}>
      <div style={RP.header}>
        <span style={{ fontSize: "14px" }}>↩</span>
        <span style={RP.headerText}>Returning Patients</span>
        <span style={RP.badge}>{returning.length}</span>
      </div>
      <div style={RP.hint}>
        These patients are returning from another station. Call them directly without waiting through the normal queue.
      </div>
      {returning.map((r, i) => (
        <div key={r.id} style={RP.row}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
              <span style={{ fontSize: "15px", fontWeight: "800", color: "#F97316", fontVariantNumeric: "tabular-nums" }}>
                {r.ticket_number}
              </span>
              {r.priority !== "normal" && (
                <span style={{ fontSize: "11px", color: r.priority === "emergency" ? "#EF4444" : "#EAB308" }}>
                  {r.priority === "emergency" ? "🚨" : "⚡"}
                </span>
              )}
            </div>
            <div style={{ fontSize: "12px", color: "#E2E8F0", fontWeight: "500" }}>{r.patient_name}</div>
            <div style={{ fontSize: "11px", color: "#64748B", marginTop: "2px" }}>
              From: <span style={{ color: "#94A3B8" }}>{r.from_station}</span>
            </div>
          </div>
          <button
            onClick={() => onCall(r.id)}
            disabled={calling || isAway}
            style={{
              background: "rgba(249,115,22,0.15)",
              border: "1px solid rgba(249,115,22,0.3)",
              borderRadius: "8px", color: "#F97316",
              fontSize: "11px", fontWeight: "700",
              padding: "6px 12px", cursor: calling || isAway ? "not-allowed" : "pointer",
              fontFamily: "inherit", opacity: calling || isAway ? 0.5 : 1,
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Call ▶
          </button>
        </div>
      ))}
    </div>
  );
}

const RP = {
  root:       { background: "rgba(249,115,22,0.05)", border: "1px solid rgba(249,115,22,0.2)", borderRadius: "12px", overflow: "hidden", marginTop: "12px" },
  header:     { display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", background: "rgba(249,115,22,0.1)", borderBottom: "1px solid rgba(249,115,22,0.15)" },
  headerText: { fontSize: "11px", fontWeight: "800", color: "#F97316", letterSpacing: "0.08em", textTransform: "uppercase", flex: 1 },
  badge:      { background: "#F97316", color: "#fff", fontSize: "11px", fontWeight: "700", borderRadius: "10px", padding: "1px 7px", minWidth: "20px", textAlign: "center" },
  hint:       { fontSize: "11px", color: "#92400E", padding: "8px 14px", background: "rgba(249,115,22,0.06)", borderBottom: "1px solid rgba(249,115,22,0.1)", lineHeight: "1.5" },
  row:        { display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: "1px solid rgba(249,115,22,0.08)" },
};

const styles = {
  root:         { padding:"24px 28px", fontFamily:"'DM Sans','Segoe UI',sans-serif", minHeight:"100vh", background:"#060D1F" },
  noStation:    { display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"70vh", gap:"0" },
  header:       { display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:"24px", flexWrap:"wrap", gap:"12px", paddingBottom:"16px" },
  title:        { fontSize:"24px", fontWeight:"700", color:"#fff", margin:"0 0 4px", letterSpacing:"-0.4px" },
  sub:          { fontSize:"14px", color:"#64748B", margin:0, display:"flex", alignItems:"center", gap:"8px" },
  stationCode:  { background:"rgba(37,99,235,0.15)", color:"#60A5FA", borderRadius:"5px", padding:"2px 7px", fontSize:"11px", fontWeight:"700", fontFamily:"monospace" },
  headerActions:{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" },
  waitingBadge: { display:"flex", flexDirection:"column", alignItems:"center", background:"rgba(15,23,42,0.8)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:"10px", padding:"8px 16px", minWidth:"64px" },
  waitingNum:   { fontSize:"22px", fontWeight:"700", color:"#60A5FA", lineHeight:1 },
  waitingLabel: { fontSize:"10px", color:"#475569", fontWeight:"600", letterSpacing:"0.06em", textTransform:"uppercase", marginTop:"2px" },
  layout:       { display:"grid", gridTemplateColumns:"1fr 320px", gap:"20px", alignItems:"start" },
  leftCol:{}, rightCol:{},
};
const SP = {
  root:         { background:"rgba(15,23,42,0.8)", border:"1px solid rgba(37,99,235,0.2)", borderRadius:"18px", padding:"28px 24px", display:"flex", flexDirection:"column", gap:"16px", minHeight:"400px" },
  label:        { fontSize:"10px", fontWeight:"700", color:"#2563EB", letterSpacing:"0.12em", textTransform:"uppercase" },
  ticketNum:    { fontSize:"64px", fontWeight:"800", color:"#fff", letterSpacing:"-2px", lineHeight:1, fontVariantNumeric:"tabular-nums" },
  priorityBadge:{ display:"inline-flex", alignItems:"center", gap:"6px", borderRadius:"8px", padding:"5px 12px", fontSize:"12px", fontWeight:"700", letterSpacing:"0.06em" },
  patientCard:  { background:"rgba(30,41,59,0.6)", borderRadius:"12px", padding:"14px 16px", display:"flex", flexDirection:"column", gap:"6px" },
  patientName:  { fontSize:"18px", fontWeight:"700", color:"#F1F5F9" },
  patientDetail:{ fontSize:"13px", color:"#64748B" },
  journey:      { display:"flex", flexDirection:"column", gap:"4px", borderLeft:"2px solid rgba(37,99,235,0.2)", paddingLeft:"12px" },
  journeyStep:  { display:"flex", alignItems:"center", gap:"8px" },
  journeyDot:   { width:"8px", height:"8px", borderRadius:"50%", flexShrink:0 },
  journeyStation:{ fontSize:"12px", color:"#94A3B8", fontWeight:"500" },
  journeyOutcome:{ fontSize:"11px", color:"#475569" },
  actions:      { display:"flex", gap:"8px", marginTop:"auto" },
  idle:         { flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"20px 0" },
  idleIcon:     { fontSize:"44px", marginBottom:"12px" },
  idleText:     { fontSize:"22px", fontWeight:"700", color:"#E2E8F0", marginBottom:"6px" },
  idleSub:      { fontSize:"14px", color:"#64748B" },
};
const WL = {
  root:         { background:"rgba(15,23,42,0.6)", border:"1px solid rgba(37,99,235,0.15)", borderRadius:"16px", overflow:"hidden" },
  header:       { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", borderBottom:"1px solid rgba(37,99,235,0.1)" },
  title:        { fontSize:"12px", fontWeight:"700", color:"#475569", letterSpacing:"0.08em", textTransform:"uppercase" },
  count:        { background:"rgba(37,99,235,0.15)", color:"#60A5FA", borderRadius:"12px", padding:"2px 10px", fontSize:"13px", fontWeight:"700" },
  empty:        { padding:"32px", textAlign:"center", color:"#334155", fontSize:"13px" },
  list:         { maxHeight:"calc(100vh - 220px)", overflowY:"auto" },
  row:          { display:"flex", alignItems:"center", gap:"10px", padding:"11px 18px", borderBottom:"1px solid rgba(37,99,235,0.05)", paddingLeft:"15px" },
  position:     { width:"22px", height:"22px", borderRadius:"6px", background:"rgba(51,65,85,0.4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"11px", fontWeight:"700", color:"#64748B", flexShrink:0 },
  info:         { flex:1, minWidth:0 },
  ticketNum:    { fontSize:"13px", fontWeight:"700", color:"#E2E8F0" },
  name:         { fontSize:"12px", color:"#64748B", marginTop:"1px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  meta:         { fontSize:"10px", color:"#334155" },
  right:        { display:"flex", flexDirection:"column", alignItems:"flex-end", gap:"3px", flexShrink:0 },
  priorityChip: { fontSize:"14px" },
  accessChip:   { fontSize:"14px" },
  waited:       { fontSize:"11px", color:"#334155", fontVariantNumeric:"tabular-nums" },
};
const TM = {
  patientBar:   { display:"flex", alignItems:"center", gap:"12px", background:"rgba(37,99,235,0.08)", border:"1px solid rgba(37,99,235,0.15)", borderRadius:"10px", padding:"10px 14px" },
  ticketNum:    { fontSize:"20px", fontWeight:"800", color:"#60A5FA", letterSpacing:"-0.5px" },
  patientName:  { fontSize:"15px", fontWeight:"600", color:"#E2E8F0" },
  outcomeGrid:  { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px" },
  outcomeBtn:   { padding:"12px 14px", borderRadius:"10px", cursor:"pointer", textAlign:"left", transition:"border-color 0.15s, background 0.15s", fontFamily:"inherit" },
  outcomeLabelEn:{ fontSize:"14px", fontWeight:"600", color:"#E2E8F0", marginBottom:"4px" },
  outcomeMeta:  { fontSize:"11px", color:"#64748B" },
};
